import { extractImageFrames, clampTo } from '../../tools';
import type { GeneralModelConfig } from '../../general';

export const minimaxConfigs: Record<string, GeneralModelConfig> = {
    // https://fal.ai/models/minimax/h3-max/text-to-video
    // https://fal.ai/models/minimax/h3-max/image-to-video
    // https://fal.ai/models/minimax/h3-max/reference-to-video
    'minimax-h3-max': {
        apiPath: ({ frameMode }) => {
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/minimax/h3-max/image-to-video';
            } else if (frameMode === 'ref') {
                return '/minimax/h3-max/reference-to-video';
            } else {
                return '/minimax/h3-max/text-to-video';
            }
        },
        pollApiPath: '/minimax/h3-max/requests/{taskId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = (
                (videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []
            ).filter(Boolean).slice(0, 3);
            const audios = (
                (audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []
            ).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    prompt: prompt.slice(0, 50000),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], 5),
                    resolution: clampTo((quality || '768P').toUpperCase(), ['480P', '768P'] as const, '768P'),
                    seed: typeof providerOptions?.seed === 'number' && Number.isFinite(providerOptions?.seed) ? providerOptions?.seed : undefined,
                    // 首帧 URL
                    image_url: firstImage!,
                    // 可选参数
                    prompt_expansion_mode: clampTo((providerOptions as any)?.prompt_expansion_mode ?? 'balanced', ['balanced', 'quality'] as const, 'balanced'),
                    enable_safety_checker: false,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    prompt: prompt.slice(0, 50000),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], 5),
                    resolution: clampTo((quality || '768P').toUpperCase(), ['480P', '768P'] as const, '768P'),
                    seed: typeof providerOptions?.seed === 'number' && Number.isFinite(providerOptions?.seed) ? providerOptions?.seed : undefined,
                    // 首帧 URL
                    image_url: firstImage!,
                    // 尾帧 URL
                    end_image_url: lastImage || firstImage!,
                    // 可选参数
                    prompt_expansion_mode: clampTo((providerOptions as any)?.prompt_expansion_mode ?? 'balanced', ['balanced', 'quality'] as const, 'balanced'),
                    enable_safety_checker: false,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt.slice(0, 50000),
                    aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], 5),
                    resolution: clampTo((quality || '768P').toUpperCase(), ['480P', '768P'] as const, '768P'),
                    seed: typeof providerOptions?.seed === 'number' && Number.isFinite(providerOptions?.seed) ? providerOptions?.seed : undefined,
                    // 图片 URL
                    ...(images.length > 0 ? { reference_image_urls: images } : {}),
                    // 视频 URL
                    ...(videos.length > 0 ? { reference_video_urls: videos } : {}),
                    // 音频 URL
                    ...(audios.length > 0 ? { reference_audio_urls: audios } : {}),
                    // 可选参数
                    prompt_expansion_mode: clampTo((providerOptions as any)?.prompt_expansion_mode ?? 'balanced', ['balanced', 'quality'] as const, 'balanced'),
                    // system
                    enable_safety_checker: false,
                };
            // 文生视频模式
            } else {
                return {
                    prompt: prompt.slice(0, 50000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], 5),
                    resolution: clampTo((quality || '768P').toUpperCase(), ['480P', '768P'] as const, '768P'),
                    // 可选参数
                    prompt_expansion_mode: clampTo((providerOptions as any)?.prompt_expansion_mode ?? 'balanced', ['balanced', 'quality'] as const, 'balanced'),
                    seed: typeof providerOptions?.seed === 'number' && Number.isFinite(providerOptions?.seed) ? providerOptions?.seed : undefined,
                    enable_safety_checker: false,
                };
            }
        },
    },
};
